'use client';

import { authClient } from '@/api/client/auth.client';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { triggerContractGateOnFirstLogin } from '@/shared/lib/auth/first-login';
import { isTwoFactorRequired } from '@/shared/lib/auth/two-factor';
import { captureError } from '@/shared/lib/errors/capture';
import { useAuthStore } from '@/shared/stores/auth-store';
import { cn } from '@/shared/utils/formatting';
import type { User } from '@/types';
import { AxiosError } from 'axios';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

interface OtpCodeFormProps {
  email: string;
  onBack: () => void;
  onSuccess: () => void;
  onTwoFactor: (challengeToken: string) => void;
}

const CELL_BASE =
  "w-full h-14 bg-transparent border-0 border-b-2 border-[#2a1c22] rounded-none text-center text-[32px] font-semibold text-white outline-none transition-[border-color,transform] font-['JetBrains_Mono',_'SF_Mono',_ui-monospace,_monospace] focus:border-red-primary focus:-translate-y-px disabled:opacity-70";
const CELL_FILLED = 'border-red-primary';
const CELL_ERR = 'border-red-primary';
const PRIMARY =
  'w-full rounded-[2px] bg-red-primary text-white uppercase tracking-[0.1em] text-xs font-bold py-[17px] transition-colors hover:bg-red-hover active:bg-[#a51420] disabled:opacity-55 disabled:cursor-not-allowed flex items-center justify-center gap-2';

export function OtpCodeForm({ email, onBack, onSuccess, onTwoFactor }: OtpCodeFormProps) {
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);
  const cellRefs = useRef<Array<HTMLInputElement | null>>([]);
  const verifyLockRef = useRef(false);

  useEffect(() => {
    cellRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const code = digits.join('');
  const complete = code.length === CODE_LENGTH && digits.every((d) => d !== '');

  const verify = useCallback(async () => {
    if (verifyLockRef.current) return;
    verifyLockRef.current = true;
    setVerifying(true);
    setError(null);
    try {
      const response = await authClient.otpVerify({ email, otp: code });
      if (isTwoFactorRequired(response)) {
        onTwoFactor(response.challenge_token);
        return;
      }
      useAuthStore.setState({
        user: response.user as unknown as User,
        isSubscribed: response.subscribed ?? true,
        isAuthenticated: true,
        isInitialized: true,
      });
      posthog.capture(
        AnalyticsEvent.AUTH_OTP_VERIFIED,
        { email_domain: email.split('@')[1] ?? null },
        { send_instantly: true }
      );
      triggerContractGateOnFirstLogin({
        last_login: response.last_login,
        user: response.user,
      });
      onSuccess();
    } catch (err) {
      verifyLockRef.current = false;
      setVerifying(false);
      if (
        err instanceof AxiosError &&
        (err.response?.status === 422 || err.response?.status === 400)
      ) {
        // Invalid/expired code is expected user input, not an actionable error.
        // Keep the signal in Sentry (spike detection) but tag as noise.
        captureError(err, {
          component: 'OtpCodeForm',
          action: 'otp-verify',
          severity: 'warning',
          tags: { signal: 'noise' },
          skipPostHog: true,
        });
        setError('Invalid or expired code. Please try again.');
      } else if (err instanceof AxiosError && err.response?.status === 429) {
        captureError(err, {
          component: 'OtpCodeForm',
          action: 'otp-verify',
          severity: 'warning',
          tags: { signal: 'noise' },
          skipPostHog: true,
        });
        setError('Too many attempts. Please wait a moment.');
      } else {
        captureError(err, { component: 'OtpCodeForm', action: 'otp-verify' });
        setError('Something went wrong. Please try again.');
      }
      setDigits(Array(CODE_LENGTH).fill(''));
      cellRefs.current[0]?.focus();
    }
  }, [code, email, onSuccess, onTwoFactor]);

  useEffect(() => {
    if (!complete || verifying) return;
    const t = setTimeout(verify, 250);
    return () => clearTimeout(t);
  }, [complete, verifying, verify]);

  const setCellAt = (index: number, value: string) => {
    const sanitized = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = sanitized;
      return next;
    });
    if (sanitized && index < CODE_LENGTH - 1) {
      cellRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      cellRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      cellRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      cellRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    const focusIdx = Math.min(text.length, CODE_LENGTH - 1);
    cellRefs.current[focusIdx]?.focus();
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    try {
      await authClient.otpSend(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 429) {
        captureError(err, {
          component: 'OtpCodeForm',
          action: 'otp-resend',
          severity: 'warning',
          tags: { signal: 'noise' },
          skipPostHog: true,
        });
        setError('Please wait a moment before resending.');
      } else if (err instanceof AxiosError && err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        captureError(err, { component: 'OtpCodeForm', action: 'otp-resend' });
        setError('Could not resend code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3.5 my-8">
      <div className="text-center">
        <div className="relative inline-flex items-center justify-center mb-3.5 text-white">
          <Mail className="h-10 w-10" />
          <span className="absolute -top-0.5 -right-1.5 h-3 w-3 rounded-full bg-red-primary border-2 border-[#0a0608] animate-pulse" />
        </div>
        <h2 className="text-[22px] font-bold tracking-[-0.01em] text-white">Check your inbox</h2>
        <p className="mt-1.5 text-[13.5px] leading-[1.55] text-text-secondary">
          We sent a 6-digit code to <span className="font-semibold text-white">{email}</span>.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-2 ml-2 inline-flex items-center gap-1 text-[13px] font-medium text-red-primary transition-colors hover:text-red-hover hover:underline underline-offset-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Change email
        </button>
      </div>

      <div className="grid grid-cols-6 gap-3.5 my-3.5" onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              cellRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            disabled={verifying}
            onChange={(e) => setCellAt(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            aria-label={`Digit ${index + 1}`}
            className={cn(
              `${CELL_BASE} ${digit ? CELL_FILLED : ''} ${error ? CELL_ERR : ''}`,
              'focus-visible:outline-0! focus-visible:outline-offset-0!'
            )}
          />
        ))}
      </div>

      {error && <p className="text-center text-[12.5px] text-red-primary">{error}</p>}

      <p className="text-center text-[12.5px] text-text-tertiary">
        Can&apos;t find it? Check your spam folder.
      </p>

      <div className="min-h-5 text-center text-[13px] text-text-secondary">
        {cooldown > 0 ? (
          <span>
            Resend code in <span className="tabular-nums text-text-primary">{cooldown}s</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={resend}
            disabled={resending}
            className="bg-transparent text-text-secondary transition-colors hover:text-white disabled:cursor-default"
          >
            {resending ? (
              'Resending…'
            ) : (
              <>
                Didn&apos;t get it?{' '}
                <span className="font-medium text-red-primary">Resend code</span>
              </>
            )}
          </button>
        )}
      </div>

      <button type="button" onClick={verify} disabled={!complete || verifying} className={PRIMARY}>
        {verifying ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Verifying
          </>
        ) : (
          'Verify & sign in'
        )}
      </button>
    </div>
  );
}
